import { OptionRow } from '../../primitives';
import {
  StepCta,
  StepHeading,
  StepStack,
  StepSub,
  TertiaryLink,
} from '../../OnboardingShell/StepLayout';
import { JOURNEY_OPTIONS } from '../../../fixtures/onboarding';
import type { OnboardingAnswers } from '../../../hooks/onboardingReducer';

type Step1Props = {
  answers: OnboardingAnswers;
  setAnswer: (patch: Partial<OnboardingAnswers>) => void;
  onContinue: () => void;
  /** P7(a) — when provided (flag coach_provisioning_v1 on), renders the
   *  "Coaching a team?" secondary path below the athlete cards. */
  onCoachPath?: () => void;
};

/**
 * Step 1 — Journey selection.
 *
 * The structural fix that the Phase 2 redesign makes: `_journey` was
 * previously captured pre-onboarding (sports.html `journey-selection-overlay`)
 * but Step 5+6 ignored it. Moving it INTO the flow as Step 1 means the
 * value is available immediately for the Step 5 + Step 6 branches.
 *
 * Per the Phase 2 plan, Phase 2 writes the journey to localStorage
 * only — no Supabase write. Phase 7 cutover wires
 * `handleJourneySelection()` from auth.js:374 to also persist to
 * `athleteContext`.
 *
 * Escape hatch ("Not sure yet — start with health & fitness") is
 * preserved from the vanilla overlay. Clicking it picks
 * `health_fitness` and advances in one action.
 */
export function Step1Journey({ answers, setAnswer, onContinue, onCoachPath }: Step1Props) {
  return (
    <>
      <StepHeading>What brings you to running?</StepHeading>
      <StepSub>
        This shapes everything: your plan, your coaching voice, your dashboard.
      </StepSub>

      <StepStack>
        {JOURNEY_OPTIONS.map((j) => (
          <OptionRow
            key={j.id}
            label={j.label}
            sub={j.description}
            selected={answers.journey === j.id}
            onClick={() => setAnswer({ journey: j.id })}
          />
        ))}
      </StepStack>

      <StepCta onClick={onContinue} disabled={!answers.journey}>
        Continue
      </StepCta>

      <TertiaryLink
        onClick={() => {
          setAnswer({ journey: 'health_fitness' });
          onContinue();
        }}
      >
        Not sure yet? Start with health &amp; fitness. Change anytime.
      </TertiaryLink>

      {onCoachPath && (
        <TertiaryLink onClick={onCoachPath}>
          Coaching a team? Set up your team instead →
        </TertiaryLink>
      )}
    </>
  );
}
